/**
 * M1-27 — the installer (§7.5).
 *
 * The acceptance is a distance: *a paid owner reaches an installer in two
 * clicks from login.* Login lands on the overview, so this page is one click
 * away in the rail and the download is the second. Nothing else on it may get
 * between those two — the version, the hash and the notes are all below the
 * button rather than above it.
 *
 * The hash is shown because §7.5 asks for it and because it is the only thing
 * an owner can check against a file they downloaded over a connection they do
 * not trust. It is useless without instructions, so the command to produce it
 * is printed next to it.
 */

import { useEffect, useState } from 'react'
import { Link } from 'react-router'

import { api, type ReleaseView, type Subscription } from '../api.js'
import { date, megabytes } from '../format.js'
import { Alert, Card, Pill, Row, Rows } from '../components/ui.js'
import { messageFor } from './Register.js'

export function Download() {
  const [release, setRelease] = useState<ReleaseView | null>(null)
  const [subscription, setSubscription] = useState<Subscription | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    void Promise.all([api.currentRelease(), api.subscription()])
      .then(([r, s]) => {
        setRelease(r)
        setSubscription(s)
      })
      .catch((err: unknown) => setError(messageFor(err)))
  }, [])

  async function copyHash(sha256: string) {
    try {
      await navigator.clipboard.writeText(sha256)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard access is refused in plenty of ordinary situations — an
      // insecure origin, a permission the browser never asked about. The hash
      // is on screen and selectable, so this is a convenience failing, not the
      // feature failing, and an error banner would be out of proportion.
    }
  }

  if (error) return <Alert>{error}</Alert>
  if (!release || !subscription) return <p className="empty">Loading…</p>

  if (!release.release) {
    return (
      <Card title="Till software">
        <p className="empty">
          No version has been published yet. This is where the installer will appear.
        </p>
      </Card>
    )
  }

  const build = release.release

  if (!release.canDownload) {
    return (
      <Card title="Till software" action={<Pill tone="warn">Not available yet</Pill>}>
        <p>
          {subscription.status === 'trial'
            ? 'The till software is not available during a phone trial — use the mobile app to sell.'
            : subscription.status === 'unpaid'
              ? 'The till software is released once your first month is paid.'
              : 'Your cover ran out, so the installer is not available at the moment.'}
        </p>
        <p className="card__note">
          This is deliberate rather than a restriction for its own sake: installing the till and
          only then discovering it will not run — after paying someone to set the machine up — is
          a worse afternoon than this page.
        </p>
        <div className="actions">
          <Link to="/billing">
            <button type="button" className="primary">
              {subscription.status === 'unpaid' ? 'Pay for your first month' : 'Pay now'}
            </button>
          </Link>
        </div>
      </Card>
    )
  }

  return (
    <>
      <Card
        title="Till software"
        action={<Pill tone="ok">{build.version}</Pill>}
        note="One installer serves every business. Run it on the shop PC and sign in there to set the machine up."
      >
        <div className="actions">
          {/* A plain anchor, not a router link and not fetch: the response is
              a file with a content-disposition, and the browser's own download
              handling is better than anything reimplemented here. */}
          <a href="/api/releases/current/download" download>
            <button type="button" className="primary wide">
              Download installer ({megabytes(build.sizeBytes)})
            </button>
          </a>
        </div>

        <Rows>
          <Row label="Version">{build.version}</Row>
          <Row label="Size">{megabytes(build.sizeBytes)}</Row>
          <Row label="Published">{date(build.releasedAt)}</Row>
        </Rows>
      </Card>

      {build.notes ? (
        <Card title="What changed">
          <p className="notes">{build.notes}</p>
        </Card>
      ) : null}

      <Card
        title="Checking the file"
        note="Optional. Worth doing if you downloaded over a connection you do not control."
      >
        <p className="card__note" style={{ marginTop: 0 }}>
          In PowerShell, in the folder you saved it to:
        </p>
        <pre className="mono command">Get-FileHash .\propene-setup-{build.version}.exe</pre>
        <p className="card__note">The result should match this exactly:</p>
        <pre className="mono hash">{build.sha256}</pre>
        <div className="actions">
          <button type="button" className="small" onClick={() => void copyHash(build.sha256)}>
            {copied ? 'Copied' : 'Copy hash'}
          </button>
        </div>
        <p className="card__note">
          If it does not match, the file was damaged or altered on the way down. Delete it and
          download again rather than running it.
        </p>
      </Card>

      <Card title="Setting up the machine">
        <ol className="steps">
          <li>Run the installer on the PC that will be your till.</li>
          <li>Sign in with this account's email and password when it asks.</li>
          <li>
            The machine takes your one device slot. If you replace it later, release the old one
            on the <Link to="/devices">Devices</Link> page first.
          </li>
        </ol>
      </Card>
    </>
  )
}
